import { Controller } from "@hotwired/stimulus"

// The exhibitors directory's search and zone chips (docs/07).
//
// Filtering happens over rows that are already on the page. A directory of a
// hundred and fifty stands is small, and a round trip per keystroke on a
// venue's wifi is exactly what a visitor standing in the park does not need.
// The server renders every stand once; this hides what does not match.
//
// Two things it must get right, because both are invisible when wrong:
// the count has to describe what is actually on screen, and a filter that
// matches nothing has to SAY so — an empty grid with no explanation reads as
// a broken page.
export default class extends Controller {
  static targets = ["search", "clear", "count", "empty", "zones", "controls"]

  connect() {
    this.zone = "all"
    this.markEdges = this.markEdges.bind(this)
    this.markStuck = this.markStuck.bind(this)
    this.measureNav = this.measureNav.bind(this)
    window.addEventListener("resize", this.markEdges)
    window.addEventListener("resize", this.measureNav)
    // Capture, so a scroll on whichever box scrolls the page is heard.
    document.addEventListener("scroll", this.markStuck, { capture: true, passive: true })
    // A reload restores the scroll position after the first paint, and the
    // back/forward cache brings the page back already scrolled; either way
    // the bar is pinned before any scroll of the reader's own.
    window.addEventListener("load", this.markStuck)
    window.addEventListener("pageshow", this.markStuck)
    this.measureNav()
    this.showView(this.rememberedView() || "cards")
    this.filter()
  }

  disconnect() {
    window.removeEventListener("resize", this.markEdges)
    window.removeEventListener("resize", this.measureNav)
    document.removeEventListener("scroll", this.markStuck, { capture: true })
    window.removeEventListener("load", this.markStuck)
    window.removeEventListener("pageshow", this.markStuck)
  }

  // The search and chips pin under the nav once the page scrolls past them,
  // and their ground shows only while pinned. The pin line is the nav's real
  // height (its padding changes at 640px); the CSS fallback covers the moment
  // before this runs.
  measureNav() {
    if (!this.hasControlsTarget) return
    const nav = document.getElementById("topbar")
    if (nav) this.controlsTarget.style.setProperty("--nav-h", `${nav.offsetHeight}px`)
    this.pinnedAt = parseFloat(getComputedStyle(this.controlsTarget).top) || 0
    this.markStuck()
  }

  // Runs on every scroll, synchronously: deferring it to the next frame was
  // the moment the stands showed through a pinned bar with no ground yet. It
  // costs one rectangle read, and writes only when the state changes.
  markStuck() {
    if (!this.hasControlsTarget) return
    // Pinned once the band the controls sit at the top of has moved above the
    // pin line. Measured from the band, not scrollY, which stays 0 when the
    // page scrolls a box other than the window.
    const stuck = this.element.getBoundingClientRect().top < this.pinnedAt - 0.5
    if (stuck !== this.controlsTarget.hasAttribute("data-stuck")) {
      this.controlsTarget.toggleAttribute("data-stuck", stuck)
    }
  }

  // A filter changed while pinned would leave the reader mid-page over
  // whatever now sits there: bring the top of the results up under the bar.
  revealResults() {
    if (!this.hasControlsTarget || !this.hasZonesTarget) return
    if (!this.controlsTarget.hasAttribute("data-stuck")) return
    const target = this.zonesTarget.getBoundingClientRect().top + window.scrollY -
      this.controlsTarget.getBoundingClientRect().bottom - 8
    if (window.scrollY > target) window.scrollTo({ top: target })
  }

  // The list view rounds the logo strip's ends on the first and last row
  // showing in EACH column. CSS cannot see columns, so this stamps
  // data-edge="first|last" after every filter, view switch and resize. A
  // sponsor row is a group of its own above the rest, so each is rounded at
  // both ends and every column below starts a strip of its own.
  markEdges() {
    this.element.querySelectorAll("[data-zgrid]").forEach((grid) => {
      const columns = new Map()
      grid.querySelectorAll("[data-exhibitor]").forEach((card) => {
        delete card.dataset.edge
        if (card.hidden) return
        if (card.classList.contains("sponsor")) { card.dataset.edge = "first last"; return }
        if (!columns.has(card.offsetLeft)) columns.set(card.offsetLeft, [])
        columns.get(card.offsetLeft).push(card)
      })
      ;[...columns.keys()].sort((a, b) => a - b).forEach((left) => {
        const rows = columns.get(left)
        rows[0].dataset.edge = "first"
        const last = rows[rows.length - 1]
        last.dataset.edge = `${last.dataset.edge || ""} last`.trim()
      })
    })
  }

  // Cards or list. One attribute on #zones; the design's CSS does the layout,
  // so the filter above never knows which view is showing.
  view(event) {
    this.showView(event.currentTarget.dataset.view)
    this.revealResults()
    try { localStorage.setItem("directory-view", this.zonesTarget.dataset.view) } catch {}
  }

  showView(name) {
    if (!this.hasZonesTarget) return
    this.zonesTarget.dataset.view = name
    this.element.querySelectorAll(".viewtoggle [data-view]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.view === name))
    })
    this.markEdges()
  }

  rememberedView() {
    try {
      const saved = localStorage.getItem("directory-view")
      return ["cards", "list"].includes(saved) ? saved : null
    } catch {
      return null
    }
  }

  choose(event) {
    const chip = event.currentTarget
    this.zone = chip.dataset.filter
    this.element.querySelectorAll("[data-filter]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button === chip))
    })
    this.filter()
    this.revealResults()
  }

  clear() {
    this.searchTarget.value = ""
    this.zone = "all"
    this.element.querySelectorAll("[data-filter]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.filter === "all"))
    })
    this.filter()
    this.searchTarget.focus()
  }

  filter() {
    const term = this.searchTarget.value.trim().toLowerCase()
    const cards = this.element.querySelectorAll("[data-exhibitor]")
    let shown = 0

    cards.forEach((card) => {
      const matchesZone = this.zone === "all" || card.dataset.zone === this.zone
      // Name OR stand: people look for both, and "B-07" is a perfectly good
      // thing to type when you are standing next to B-06.
      const matchesTerm = term === "" ||
        card.dataset.name.includes(term) ||
        card.dataset.stand.includes(term)

      const visible = matchesZone && matchesTerm
      card.hidden = !visible
      if (visible) shown += 1
    })

    // A band whose every card is hidden takes its heading with it, or the
    // page reads as four empty zones.
    this.element.querySelectorAll("[data-band]").forEach((band) => {
      band.hidden = band.querySelectorAll("[data-exhibitor]:not([hidden])").length === 0
    })

    this.countTarget.textContent = `Showing ${shown} of ${cards.length}`
    if (this.hasEmptyTarget) this.emptyTarget.hidden = shown > 0
    if (this.hasClearTarget) this.clearTarget.hidden = term === "" && this.zone === "all"
    this.markEdges()
  }
}
